import { api } from "../api";

const BASE_PATH = "/api/synthetic-stockmoves";

export const syntheticStockMoveService = {
  /**
   * Get purchase orders eligible for synthetic inbound creation
   * @param {number} companyId - Company ID
   * @param {number} month - Month (1-12)
   * @param {number} year - Year
   * @returns {Promise} - Promise with eligible purchase orders
   */
  getEligiblePurchaseOrders: async (companyId, month, year) => {
    const response = await api.get(`${BASE_PATH}/eligible-pos`, {
      params: { companyId, month, year }
    });
    return response.data;
  },

  /**
   * Create synthetic inbound stock moves
   * @param {Object} dto - Create synthetic inbounds DTO
   * @param {number[]} dto.POItemIds - Array of PO line item IDs
   * @param {number} dto.WarehouseId - Warehouse ID
   * @param {number} dto.CompanyId - Company ID
   * @returns {Promise} - Promise with result
   */
  createSyntheticInbounds: async (dto) => {
    const response = await api.post(`${BASE_PATH}/create-inbounds`, dto);
    return response.data;
  },

  /**
   * Get existing synthetic inbound stock moves
   * @param {number} companyId - Company ID
   * @param {Date} startDate - Start date (optional)
   * @param {Date} endDate - End date (optional)
   * @param {string} providerCode - Provider code (optional)
   * @returns {Promise} - Promise with synthetic inbounds
   */
  getSyntheticInbounds: async (companyId, startDate, endDate, providerCode) => {
    const params = { companyId };
    if (startDate) {
      params.startDate = startDate.toISOString();
    }
    if (endDate) {
      params.endDate = endDate.toISOString();
    }
    if (providerCode) {
      params.providerCode = providerCode;
    }
    const response = await api.get(`${BASE_PATH}/inbounds`, {
      params
    });
    return response.data;
  },

  /**
   * Create synthetic outbound stock moves
   * @param {Object} dto - Create synthetic outbounds DTO
   * @param {number[]} dto.StockMoveIds - Array of stock move IDs to reverse
   * @param {number} dto.CompanyId - Company ID
   * @param {Date} [dto.MoveDate] - Move date for the outbound
   * @returns {Promise} - Promise with result
   */
  createSyntheticOutbounds: async (dto) => {
    const response = await api.post(`${BASE_PATH}/create-outbounds`, dto);
    return response.data;
  },

  /**
   * Create a manual synthetic outbound stock move
   * @param {Object} dto - Create manual synthetic outbound DTO
   * @param {number} dto.CompanyId - Company ID
   * @param {number} dto.ProductId - Product ID
   * @param {number} dto.Quantity - Quantity
   * @param {number|null} [dto.UnitCost] - Unit cost (optional)
   * @param {Date} dto.MoveDate - Move date
   * @returns {Promise} - Promise with result
   */
  createManualSyntheticOutbound: async (dto) => {
    const response = await api.post(`${BASE_PATH}/outbounds`, dto);
    return response.data;
  },

  /**
   * Soft-delete a synthetic outbound stock move (sets isActive to false)
   * @param {number} id - Stock move ID
   * @returns {Promise<void>}
   */
  deleteSyntheticOutbound: async (id) => {
    await api.delete(`${BASE_PATH}/outbounds/${id}`);
  },

  /**
   * Update a synthetic outbound stock move
   * @param {number} id - Stock move ID
   * @param {Object} dto - Update synthetic outbound DTO
   * @param {number} dto.ProductId - Product ID
   * @param {number} dto.Quantity - Quantity
   * @param {number|null} dto.UnitCost - Unit cost
   * @param {Date} dto.MoveDate - Move date
   * @returns {Promise} - Promise with result
   */
  updateSyntheticOutbound: async (id, dto) => {
    const response = await api.put(`${BASE_PATH}/outbounds/${id}`, dto);
    return response.data;
  },

  /**
   * Upload a CSV of synthetic outbound stock moves
   * @param {number} companyId - Company ID
   * @param {File} file - CSV file (MoveDate, ProductCode, Quantity, UnitCost)
   * @returns {Promise<{ succeededCount: number, failedRowNumbers: number[] }>}
   */
  uploadSyntheticOutboundsCsv: async (companyId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post(
      `${BASE_PATH}/outbounds/upload-csv?companyId=${companyId}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },
};

export default syntheticStockMoveService;

